import * as PIXI from 'pixi.js';
import { ViewportState } from '../types';
import { VIEWPORT_CONSTRAINTS } from '../utils/constants';
import { Bounds } from '../utils/selection';
import { computeFitTransform } from '../utils/fitView';

export class ViewportManager {
  private viewport: PIXI.Container;
  private state: ViewportState;

  constructor(viewport: PIXI.Container) {
    this.viewport = viewport;
    this.state = {
      x: 0,
      y: 0,
      scale: 1,
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      targetX: 0,
      targetY: 0,
      targetScale: 1
    };
  }

  getState(): ViewportState {
    return { ...this.state };
  }

  updateTransform(deltaTime: number): void {
    const lerpFactor = Math.min(1, deltaTime * VIEWPORT_CONSTRAINTS.LERP_FACTOR);
    
    // Interpolate position
    this.state.x += (this.state.targetX - this.state.x) * lerpFactor;
    this.state.y += (this.state.targetY - this.state.y) * lerpFactor;
    
    // Interpolate scale
    this.state.scale += (this.state.targetScale - this.state.scale) * lerpFactor;
    
    // Apply transform to viewport
    this.viewport.x = this.state.x;
    this.viewport.y = this.state.y;
    this.viewport.scale.x = this.state.scale;
    this.viewport.scale.y = this.state.scale;
  }

  pan(deltaX: number, deltaY: number): void {
    this.state.targetX += deltaX;
    this.state.targetY += deltaY;
  }

  zoom(mouseX: number, mouseY: number, wheelDelta: number): void {
    const wheel = wheelDelta < 0 ? 1 : -1;
    const zoom = Math.exp(wheel * VIEWPORT_CONSTRAINTS.ZOOM_INTENSITY);
    
    const newScale = Math.max(
      VIEWPORT_CONSTRAINTS.MIN_SCALE,
      Math.min(VIEWPORT_CONSTRAINTS.MAX_SCALE, this.state.targetScale * zoom)
    );
    
    // Calculate zoom center offset
    const worldPosX = (mouseX - this.state.targetX) / this.state.targetScale;
    const worldPosY = (mouseY - this.state.targetY) / this.state.targetScale;
    
    this.state.targetScale = newScale;
    this.state.targetX = mouseX - worldPosX * newScale;
    this.state.targetY = mouseY - worldPosY * newScale;
  }

  reset(): void {
    this.state.targetX = 0;
    this.state.targetY = 0;
    this.state.targetScale = 1;
  }

  /**
   * Pans and zooms so `bounds` (world coordinates) fits within a viewport of the
   * given pixel size, animating there via the usual lerp. Fitting never zooms in
   * past {@link VIEWPORT_CONSTRAINTS.FIT_MAX_SCALE}.
   */
  fitToView(bounds: Bounds, viewWidth: number, viewHeight: number): void {
    const { x, y, scale } = computeFitTransform(bounds, viewWidth, viewHeight, {
      padding: VIEWPORT_CONSTRAINTS.FIT_PADDING,
      minScale: VIEWPORT_CONSTRAINTS.MIN_SCALE,
      maxScale: Math.min(VIEWPORT_CONSTRAINTS.MAX_SCALE, VIEWPORT_CONSTRAINTS.FIT_MAX_SCALE)
    });
    this.state.targetX = x;
    this.state.targetY = y;
    this.state.targetScale = scale;
  }

  screenToWorld(screenX: number, screenY: number): { x: number; y: number } {
    const worldX = (screenX - this.state.x) / this.state.scale;
    const worldY = (screenY - this.state.y) / this.state.scale;
    return { x: worldX, y: worldY };
  }

  worldToScreen(worldX: number, worldY: number): { x: number; y: number } {
    const screenX = worldX * this.state.scale + this.state.x;
    const screenY = worldY * this.state.scale + this.state.y;
    return { x: screenX, y: screenY };
  }
}